/**
 * The content tree, in the URL.
 *
 * What is open is addressable: reload and your panes come back, open a second
 * window on a different set, send someone the link and they get the same files
 * and conversations. It replaces `?file=<docId>`, which could only ever say one
 * document because the canvas could only ever hold one.
 *
 * ONE SOURCE OF TRUTH, deliberately. Sizes ride along with the shape rather than
 * living in localStorage, because splitting them means two places to read and a
 * reconciliation whenever they disagree — and "which panes" and "how big" are the
 * same fact from the user's side: this is what my screen looks like.
 *
 * NOT THE SHARE MECHANISM. Sharing a session or a file gets its own flow with its
 * own permissions; a url that happens to name some documents is not an access
 * grant, and nothing here should be read as one. Ids that the reader cannot open
 * simply do not render (see `pruneMissing`).
 *
 * THE ENCODING is a small grammar, not JSON-in-base64, so a url stays legible and
 * a human can see what a link will open:
 *
 *   a                          the session list, across every project
 *   a:<projectId>              one project's session list
 *   a:<projectId>/<sessionId>  an agent block on one conversation
 *   d:<projectId>/<docId>      a document block
 *   d:<projectId>/<docId>|<a>,<b>  a code pane showing docId, holding tabs a, b
 *   t:<deviceId>               one machine's shells
 *   t:<deviceId>/<shellId>     one shell on it
 *   n  /  n:<projectId>        an empty pane, not yet told what it is for
 *   r[a;b]  /  c[a;b]          a row / column split
 *   @<n>                       that child's share, two decimals, omitted at even
 *
 *   r[d:p1/abc@0.62;c[a:p1/xyz;d:p2/def]]
 *
 * THE PROJECT SEGMENT (RFC 0281) is what lets one url name panes in more than
 * one project. It also gives a bare `a` a second reading it did not have: the
 * list used to be one project's because there was only ever one, and it is now
 * the list across all of them. Links written before the segment existed no
 * longer parse, which is deliberate: a document block with no project cannot say
 * which catalog to read, and guessing would render someone else's document.
 */

import {
  type ContentNode,
  type Direction,
  leaf,
  normalize,
  normalizeSizes,
  withTabs,
} from '../../features/code/contentTree'

/** The query parameter. `file` is gone: it could only name one document. */
export const CONTENT_PARAM = 'v'

/**
 * A share, to three decimals.
 *
 * Three rather than two because the url is where a drag is STORED: a seam
 * writes the url on every pointer-move and reads it back to compute the next
 * one, so anything the encoding cannot represent is not a rounding error, it is
 * movement thrown away. At two decimals a 1px nudge of a 1000px split rounds to
 * nothing and a slow drag does not move at all, which is felt as a seam that
 * ignores you and then jumps.
 */
const round = (n: number): string => String(Math.round(n * 1000) / 1000)

/**
 * A subject id, safe inside the grammar.
 *
 * Document ids are usually opaque alphanumerics, which this leaves untouched —
 * but a WEB PAGE carries its whole url in its id (`web:https://github.com/`),
 * and that id contains the very characters the grammar is built from: `/`
 * separates project from subject, `;` separates siblings, `@` introduces a
 * size, and `[`/`]` delimit a split. Written raw, such a leaf encodes fine and
 * then fails to parse, so the pane vanishes the moment it is opened.
 *
 * encodeURIComponent is exactly the right alphabet here: it escapes every one
 * of those, and leaves an ordinary document id byte-for-byte the same, so links
 * written before this still read.
 */
const encodeSubject = (id: string): string => encodeURIComponent(id)

function encodeNode(node: ContentNode): string {
  if (node.type === 'leaf') {
    // `n` for new: a pane opened and not yet told what it is for. It has no
    // subject, so there is nothing after the project.
    if (node.content.kind === 'picker') {
      const { projectId } = node.content
      return projectId ? `n:${projectId}` : 'n'
    }
    if (node.content.kind === 'discover')
      return node.content.lineageId
        ? `p:${encodeSubject(node.content.lineageId)}`
        : 'p'
    /**
     * A terminal reads like an agent block because it IS one, in shape: a
     * device with an optional shell, exactly as a project has an optional
     * session. `t:<device>` on its own is a useful thing to send — it opens
     * that machine's terminals without asserting which one — and it is what
     * truncating the longer form gives you, which is why the tail is optional
     * here rather than a second token.
     */
    if (node.content.kind === 'terminal') {
      const { deviceId, terminalId } = node.content
      return terminalId
        ? `t:${encodeSubject(deviceId)}/${encodeSubject(terminalId)}`
        : `t:${encodeSubject(deviceId)}`
    }
    if (node.content.kind === 'agent') {
      const { projectId, sessionId } = node.content
      if (!projectId) return 'a'
      return sessionId
        ? `a:${projectId}/${encodeSubject(sessionId)}`
        : `a:${projectId}`
    }
    const { projectId, docId, tabs } = node.content
    const shown = `d:${projectId}/${encodeSubject(docId)}`
    // A code pane's tabs ride after `|`, in order. The shown one leads as it
    // always has, so a reader that ignores the tail still opens the right file.
    return tabs && tabs.length > 1
      ? `${shown}|${tabs.map(encodeSubject).join(',')}`
      : shown
  }
  const even = 1 / node.children.length
  const sizes = node.children.map((_, i) => node.sizes[i] ?? even)
  /**
   * ALL OR NOTHING: every child's share is written, or none of them are.
   *
   * Per-child omission was the bug that collapsed a pane to a sliver. Sizes like
   * [0.5, 0.25, 0.125, 0.125] — which is just what three splits of a four-pane
   * row produce — have one child sitting exactly on the even share of 0.25, so
   * that one child's `@` was dropped while its siblings kept theirs. Coming back,
   * the reader had no way to tell "even" from "unstated", took the pane as
   * unstated, and gave it the 8% floor. The pane you had just been resizing
   * collapsed, and nothing about the url looked wrong.
   *
   * A split nobody has touched still encodes bare, which is the common case and
   * what keeps a two-pane url short.
   */
  const untouched = sizes.every((s) => Math.abs(s - even) < 0.001)
  const parts = node.children.map((child, i) =>
    untouched ? encodeNode(child) : `${encodeNode(child)}@${round(sizes[i])}`,
  )
  return `${node.direction === 'row' ? 'r' : 'c'}[${parts.join(';')}]`
}

/** The tree as a url value, or null when there is nothing open. */
export function encodeContent(node: ContentNode | null): string | null {
  return node ? encodeNode(node) : null
}

/** Split on `;` at depth zero, so nested brackets stay with their child. */
function splitTop(body: string): string[] {
  const out: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < body.length; i++) {
    const ch = body[i]
    if (ch === '[') depth++
    else if (ch === ']') depth--
    else if (ch === ';' && depth === 0) {
      out.push(body.slice(start, i))
      start = i + 1
    }
  }
  out.push(body.slice(start))
  return out.filter((p) => p.length > 0)
}

interface Parsed {
  node: ContentNode
  size?: number
}

function parseNode(text: string): Parsed | null {
  // A trailing @<n> belongs to this child's share in its parent.
  let size: number | undefined
  let body = text
  const at = body.lastIndexOf('@')
  if (at > 0 && !body.slice(at).includes(']')) {
    const n = Number(body.slice(at + 1))
    if (Number.isFinite(n) && n > 0) size = n
    body = body.slice(0, at)
  }

  if (body === 'a')
    return {
      node: leaf({ kind: 'agent', projectId: null, sessionId: null }),
      size,
    }

  if (body === 'n')
    return { node: leaf({ kind: 'picker', projectId: null }), size }

  if (body === 'p')
    return { node: leaf({ kind: 'discover', lineageId: null }), size }

  if (body.startsWith('p:')) {
    try {
      const lineageId = decodeURIComponent(body.slice(2))
      return lineageId
        ? { node: leaf({ kind: 'discover', lineageId }), size }
        : null
    } catch {
      return null
    }
  }

  if (body.startsWith('n:')) {
    const projectId = body.slice(2)
    // No subject to separate, so a slash here can only be a mangled link.
    if (!projectId || projectId.includes('/')) return null
    return { node: leaf({ kind: 'picker', projectId }), size }
  }

  if (body.startsWith('t:')) {
    // Device, then optionally the shell. Same one-slash rule as the others: a
    // second slash means a hand-edited or truncated link, and a terminal leaf
    // that names the wrong machine is worse than one that does not render.
    const rest = body.slice(2)
    const slash = rest.indexOf('/')
    const rawDevice = slash === -1 ? rest : rest.slice(0, slash)
    const rawShell = slash === -1 ? '' : rest.slice(slash + 1)
    if (!rawDevice || rawShell.includes('/')) return null
    let deviceId: string
    let terminalId: string
    try {
      deviceId = decodeURIComponent(rawDevice)
      terminalId = decodeURIComponent(rawShell)
    } catch {
      return null
    }
    return {
      node: leaf({
        kind: 'terminal',
        deviceId,
        terminalId: terminalId || null,
      }),
      size,
    }
  }

  if (body.startsWith('a:') || body.startsWith('d:')) {
    // One slash splits project from subject. `indexOf`, not `split`, so a stray
    // second slash makes the value unparseable rather than being discarded.
    // A code pane's tabs, after `|`. Escaped like every subject, so neither
    // `|` nor `,` can be part of one.
    const bar = body[0] === 'd' ? body.indexOf('|') : -1
    const rawTabs = bar === -1 ? '' : body.slice(bar + 1)
    const rest = bar === -1 ? body.slice(2) : body.slice(2, bar)
    const slash = rest.indexOf('/')
    const projectId = slash === -1 ? rest : rest.slice(0, slash)
    const raw = slash === -1 ? '' : rest.slice(slash + 1)
    // A second slash is still unparseable: the subject was written escaped, so
    // a bare one can only be a hand-edited or truncated link.
    if (!projectId || raw.includes('/')) return null
    // Percent-decoding is the other half of encodeSubject. A malformed escape
    // throws rather than yielding a wrong id, and this whole leaf drops.
    let subject: string
    try {
      subject = decodeURIComponent(raw)
    } catch {
      return null
    }

    if (body[0] === 'a') {
      return {
        node: leaf({ kind: 'agent', projectId, sessionId: subject || null }),
        size,
      }
    }
    // A document needs both halves: with no project there is no catalog to read.
    if (!subject) return null
    let tabs: string[] = []
    try {
      tabs = rawTabs
        ? rawTabs.split(',').filter(Boolean).map(decodeURIComponent)
        : []
    } catch {
      tabs = []
    }
    const content = withTabs(
      { kind: 'document', projectId, docId: subject },
      tabs.includes(subject) ? tabs : [],
      subject,
    )
    return { node: leaf(content), size }
  }

  const open = body.indexOf('[')
  if (open !== 1 || !body.endsWith(']')) return null
  const direction: Direction = body[0] === 'c' ? 'column' : 'row'
  if (body[0] !== 'r' && body[0] !== 'c') return null
  const children: ContentNode[] = []
  const sizes: number[] = []
  for (const part of splitTop(body.slice(open + 1, -1))) {
    const parsed = parseNode(part)
    if (!parsed) continue
    children.push(parsed.node)
    sizes.push(parsed.size ?? 0)
  }
  if (children.length === 0) return null
  /**
   * A child with no `@` takes an equal cut of WHAT IS LEFT.
   *
   * This encoder no longer writes some shares and not others, but a link from an
   * older build or a hand-edited one can still say `r[a@0.8;b]`, and the useful
   * reading of that is "b gets the rest" — not the 8% floor this used to hand
   * out, which turned any such link into a collapsed pane. With nothing left
   * over, or nothing stated at all, an even share is the only honest answer;
   * `normalizeSizes` then clamps and rescales whatever comes out.
   */
  const even = 1 / children.length
  const stated = sizes.reduce((a, s) => a + (s > 0 ? s : 0), 0)
  const missing = sizes.filter((s) => s <= 0).length
  const rest = missing > 0 ? (1 - stated) / missing : 0
  const fallback = rest > 0 ? rest : even
  const filled = sizes.map((s) => (s > 0 ? s : fallback))
  const node = normalize({
    type: 'split',
    direction,
    children,
    sizes: normalizeSizes(filled, children.length),
  })
  return node ? { node, size } : null
}

/**
 * Read a tree out of a url value. Null for anything unparseable, so a hand-edited
 * or truncated link degrades to "nothing open" rather than a broken render — the
 * same posture every projection in this codebase takes toward untrusted input.
 *
 * Leaf IDS ARE MINTED FRESH rather than carried in the url. They are internal
 * addressing for drag and focus, meaningless to anyone else, and putting them in
 * a link would make two people opening it disagree about nothing.
 */
export function decodeContent(value: string | null): ContentNode | null {
  if (!value) return null
  const parsed = parseNode(value.trim())
  return parsed ? parsed.node : null
}
